import { memo, useEffect, useMemo } from "react";
import {
  Dropdown,
  Input,
  Button,
  Header,
  Divider,
  Form,
  FormGroup,
  FormField,
  Dimmer,
  Loader,
} from "semantic-ui-react";

// Serializers
import BroadcastPostSerializer from "@/issara-sdk/types/BroadcastPostSerializer_apps_PRX";

// Components
import BroadcastApprovalDetailPage from "./BroadcastApprovalDetail";
import BroadcastApprovalTable from "./BroadcastApprovalTable";
import ModBroadcastLog from "./ModBroadcastLog";
import ModMeForm from "./ModMeForm";
import ModBroadcastApprovalAction from "./ModBroadcastApprovalAction";

// Commons
import SnackMessage from "../common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import DateTextBox from "../common/DateTextBox";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";

// Interface
import { DEFAULT_BROADCAST_POST_LIST_LIMIT } from "./sequence/MeCast";
import { BROADCAST_CARD_NAME } from "./MeFormType";

// Types
import { BROADCAST_TYPE_OPTIONS, HEADER_ACTION_MOD, PickedState, RunSequence, SetProp, State, STATUS_BROADCAST_APPROVAL_OPTIONS } from "./MeCastType";

// Hooks
import { useBroadcastApprovalActions, useBroadcastApprovalDetail, useBroadcastApprovalFilter, useBroadcastContent, useBroadcastPreviews, useDivisionMap } from "./MeCastHook";

type CardMeCastBroadcastApprovalProps = {
  runSequence: RunSequence;
  setProp: SetProp;
  MeCastSequence: State["MeCastSequence"];
  onEvent?: any;
} & Pick<
  PickedState, "loadingStatus" | "errorMessage" | "successMessage" | "buttonLoadCheck"
>;

const ICONS = {
  broadcast: "/static/images/meform/broadcast.png",
  broadcastGray: "/static/images/meform/broadcast_gray.png",
};

const CardMeCastBroadcastApproval = (props: CardMeCastBroadcastApprovalProps) => {
  const broadcastItems = props.MeCastSequence?.broadcastListTab?.broadcastList?.items || [];
  const divisionListRaw = props.MeCastSequence?.broadcastApprovalTab?.divisionList;
  
  const divisionList = useMemo(() => {
    return Array.isArray(divisionListRaw)
      ? divisionListRaw
      : divisionListRaw?.items || [];
  }, [divisionListRaw]);

  const approvalFilter = useBroadcastApprovalFilter({
    divisionList
  });
  const approvalDetail = useBroadcastApprovalDetail({
    runSequence: props.runSequence
  });
  const approvalActions = useBroadcastApprovalActions({
    runSequence: props.runSequence,
    onActionComplete: () => {
      // todo --- refresh or do something after action
    },
  });
  const { previews } = useBroadcastPreviews({
    items: broadcastItems
  });
  const { divisionMap, updateDivisionMap, resetDivisionMap } = useDivisionMap({
    data: broadcastItems,
    divisionList,
    runSequence: props.runSequence
  });
  const { content: postContent, loading: contentLoading } = useBroadcastContent({
    firebaseId: approvalActions.selectedBroadcast?.firebase_id
  });

  useEffect(() => {
    const selectedBroadcastPost = props.MeCastSequence?.broadcastApprovalTab?.selectedBroadcast;

    if (selectedBroadcastPost) {
      approvalActions.setSelectedBroadcast(selectedBroadcastPost);
    }
  }, [props.MeCastSequence?.broadcastApprovalTab?.selectedBroadcast]);

  useEffect(() => {
    updateDivisionMap(props.MeCastSequence?.broadcastApprovalTab?.divisionDetail);
  }, [props.MeCastSequence?.broadcastApprovalTab?.divisionDetail]);

  useEffect(() => {
    if (!broadcastItems.length) {
      resetDivisionMap();
    }
  }, [broadcastItems]);

  useEffect(() => {
    props.runSequence({
      sequence: "MeCast",
      restart: true,
      broadcastParams: approvalFilter.getResolvedParams(),
    } as any);
  }, []);

  const handleSearch = () => {
    props.runSequence({
      sequence: "MeCast",
      action: "GET_BROADCAST_POST_LIST",
      broadcastParams: approvalFilter.getResolvedParams(),
    });
  };

  const handleChangePage = (activePage: number) => {
    approvalFilter.handleChangePage(activePage);

    props.runSequence({
      sequence: "MeCast",
      action: "GET_BROADCAST_POST_LIST",
      broadcastParams: approvalFilter.getResolvedParams(),
      activePage,
    });
  };

  const handleBack = () => {
    approvalDetail.closeDetail();
    approvalActions.setSelectedBroadcast(null);

    props.runSequence({
      sequence: "MeCast",
      restart: true,
      broadcastParams: approvalFilter.getResolvedParams(),
    } as any);
  };

  const groupOptions = useMemo(() => {
    const groups = broadcastItems
      .map(row => row.group_name)
      .filter(Boolean) as string[];
    const uniqueGroups = Array.from(new Set(groups));

    return uniqueGroups.map((name) => ({ key: name, text: name, value: name }));
  }, [broadcastItems]);

  const divisionOptions = useMemo(() => {
    const divisionsInOriginal = broadcastItems
      .map(broadcast => broadcast.division)
      .filter(Boolean);
    const uniqueDivisionIds = Array.from(new Set(divisionsInOriginal));

    return uniqueDivisionIds
      .map((id) => divisionList.find(d => d?.id === id))
      .filter((d): d is NonNullable<typeof d> => d != null)
      .map((d) => ({
        key: d.id,
        value: d.name,
        text: d.name,
        content: d.name,
      }));
  }, [broadcastItems, divisionList]);

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.${BROADCAST_CARD_NAME}`, null);
    props.setProp(`successMessage.${BROADCAST_CARD_NAME}`, null);
  };

  // console.log("BROADCAST APPROVAL", props);

  return (
    <div>
      <div style={{ display: "relative", padding: "15px 0", height: "100%" }}>
        {/* --- Loader --- */}
        <Dimmer active={props.loadingStatus?.[BROADCAST_CARD_NAME]} inverted>
          <Loader />
        </Dimmer>

        {/* --- ErrorMessage & SuccessMessage --- */}
        <SnackMessage
          error={props.errorMessage?.[BROADCAST_CARD_NAME]}
          success={props.successMessage?.[BROADCAST_CARD_NAME]}
          onClose={handleCloseSnackMessage}
        />

        {/* --- Actions Mod --- */}
        <ModMeForm
          // Func
          open={approvalActions.isActionModOpen}
          onClose={approvalActions.closeActionModal}
          onConfirm={approvalActions.confirmAction}
          // detail
          header={approvalActions.actionPost ? HEADER_ACTION_MOD[approvalActions.actionPost] : ""}
          content={
            <ModBroadcastApprovalAction
              action={approvalActions.actionPost}
              confirmMessage={`Are you sure you want to ${approvalActions.actionPost?.toLowerCase()} this broadcast?`}
              remark={approvalActions.rejectRemark}
              onChangeRemark={(_, data) => approvalActions.handleChangeRemark(data.value as string)}
            />
          }
        />

        {/* --- Mod Log --- */}
        <ModBroadcastLog
          // func & callback
          open={approvalDetail.isLogOpen}
          onClose={approvalDetail.closeLog}
          // data
          selectedBroadcast={approvalActions.selectedBroadcast as BroadcastPostSerializer}
          broadcastLog={props.MeCastSequence?.broadcastApprovalTab?.selectedBroadcastLog || []}
          // loader
          loader={props.loadingStatus?.broadcastLogList}
        />

        {/* --- Approval Detail --- */}
        {approvalDetail.isDetailOpen && (
          <BroadcastApprovalDetailPage
            // actions
            onBack={handleBack}
            onOpenLog={approvalDetail.openLog}
            onSelectAction={approvalActions.openActionModal}
            // data
            broadcast={approvalActions.selectedBroadcast as BroadcastPostSerializer}
            postContent={postContent}
            contentLoading={contentLoading}
            // loader
            actionLoading={props.loadingStatus?.actionBroadcast}
            // common infra
            loadingStatus={props.loadingStatus}
          />
        )}

        <div style={{ overflow: "hidden" }}>
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
              padding: "0 15px"
            }}
          >
            <Header style={{ color: "rgb(79, 79, 79)", margin: 0 }} as="h2">
              Broadcast Approval
            </Header>
          </div>
          
          <div
            style={{
              padding: "0 15px"
            }}
          >
            <Divider style={{ marginBottom: "10px", marginTop: 0 }} />
            
            <div>
              {/* --- Form --- */}
              <Form>
                <FormGroup inline>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      {STATUS_BROADCAST_APPROVAL_OPTIONS.map((status) => (
                        <button
                          key={status.key}
                          onClick={() => approvalFilter.handleToggleStatus(status.value)}
                          style={{
                            border: "1px solid #ccc",
                            backgroundColor: (approvalFilter.filter.statuses ?? []).includes(status.value) ? "#f282a0" : "white",
                            color: (approvalFilter.filter.statuses ?? []).includes(status.value) ? "white" : "black",
                            padding: "6px 12px",
                            cursor: "pointer",
                            borderRadius: 0,
                            marginRight: "-1px",
                            height: "38.21px",
                          }}
                        >
                          {status.text}
                        </button>
                      ))}
                    </div>
                  </FormField>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      <label>Submission date</label>
                    </div>
                  </FormField>
                  <FormField width={2}>
                    <DateTextBox
                      value={approvalFilter.filter.send_reviewed_start_date || ""}
                      onChange={(date) => approvalFilter.handleChangeDate("send_reviewed_start_date", date)}
                      placeholder="DD/MM/YYYY"
                      inputStyle={{ width: "100%" }}
                    />
                  </FormField>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      <label>-</label>
                    </div>
                  </FormField>
                  <FormField width={2}>
                    <DateTextBox
                      value={approvalFilter.filter.sent_reviewed_end_date || ""}
                      onChange={(date) => approvalFilter.handleChangeDate("sent_reviewed_end_date", date)}
                      placeholder="DD/MM/YYYY"
                      inputStyle={{ width: "100%" }}
                    />
                  </FormField>
                  <FormField width={3}>
                    <div style={{ width: "100%" }}>
                      <Input
                        name="broadcast_id"
                        fluid
                        placeholder="Search from Broadcast ID"
                        value={approvalFilter.filter.broadcast_id || ""}
                        onChange={approvalFilter.handleChangeData}
                      />
                    </div>
                  </FormField>
                </FormGroup>

                <FormGroup inline>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      <label>Official Account</label>
                    </div>
                  </FormField>
                  <FormField width={3}>
                    <div style={{ width: "100%" }}>
                      <SearchBoxDropdown
                        onEvent={props.onEvent}
                        id="DIVISION_SEARCH"
                        placeholder="Search by account name"
                        fluid
                        icon="search"
                        limit={20}
                        inline
                        startSearchLength={1}
                        useWithKey={false}
                        style={{ width: "100%" }}
                        searchedItemList={divisionOptions}
                        selectedItem={approvalFilter.filter.division}
                        setSelectedItem={(value) => approvalFilter.handleChangeData(null, { name: "division", value })}
                        toDisplay={(item) => item.text}
                        mapOptions={(items) => items}
                        noResultsMessage={<div>No divisions found</div>}
                        loading={false}
                      />
                    </div>
                  </FormField>
                  <FormField width={3}>
                    <div style={{ width: "100%" }}>
                      <Dropdown
                        name="group_name"
                        fluid
                        placeholder="Group Name"
                        selection
                        search
                        clearable
                        options={groupOptions}
                        value={approvalFilter.filter.group_name || ""}
                        onChange={approvalFilter.handleChangeData}
                      />
                    </div>
                  </FormField>
                  <FormField width={2}>
                    <div style={{ width: "100%" }}>
                      <Dropdown
                        name="broadcast_type"
                        fluid
                        placeholder="Broadcast type"
                        selection
                        search
                        options={BROADCAST_TYPE_OPTIONS}
                        clearable
                        value={approvalFilter.filter.broadcast_type}
                        onChange={approvalFilter.handleChangeData}
                      />
                    </div>
                  </FormField>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      <label>Broadcast date</label>
                    </div>
                  </FormField>
                  <FormField width={2}>
                    <DateTextBox
                      value={approvalFilter.filter.broadcasted_start_date || ""}
                      onChange={(date) => approvalFilter.handleChangeDate("broadcasted_start_date", date)}
                      placeholder="DD/MM/YYYY"
                      inputStyle={{ witdth: "100%" }}
                    />
                  </FormField>
                  <FormField>
                    <div style={{ width: "100%" }}>
                      <label>-</label>
                    </div>
                  </FormField>
                  <FormField width={2}>
                    <div style={{ width: "100%" }}>
                      <DateTextBox
                        value={approvalFilter.filter.broadcasted_end_date || ""}
                        onChange={(date) => approvalFilter.handleChangeDate("broadcasted_end_date", date)}
                        placeholder="DD/MM/YYYY"
                        inputStyle={{ width: "100%" }}
                      />
                    </div>
                  </FormField>
                  <FormField style={{ padding: 0 }}>
                    <div style={{ width: "100%" }}>
                      <Button
                        onClick={handleSearch}
                        style={{ backgroundColor: "#f282a0", color: "white" }}
                        icon="search"
                        loading={props.loadingStatus?.broadcastList}
                      />
                    </div>
                  </FormField>
                </FormGroup>
              </Form>
            </div>

            <BroadcastApprovalTable
              runSequence={props.runSequence}
              // func & callback
              onOpenDetail={approvalDetail.openDetail}
              onOpenLog={approvalDetail.openLog}
              // data
              data={broadcastItems}
              icon={ICONS.broadcastGray}
              broadcastApprovalTab={props.MeCastSequence?.broadcastApprovalTab}
              previews={previews}
              divisionMap={divisionMap}
            />

            {/* --- Pagination --- */}
            <div style={{ textAlign: "center" }}>
              <PaginationShorthand
                activePage={props.MeCastSequence?.broadcastListTab?.broadcastList?.activePage || 1}
                limit={DEFAULT_BROADCAST_POST_LIST_LIMIT}
                total={props.MeCastSequence?.broadcastListTab?.broadcastList?.total}
                onPageChange={handleChangePage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CardMeCastBroadcastApproval.displayName = "CardMeCastBroadcastApproval";

export default memo(CardMeCastBroadcastApproval);
